import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./SignupPage.css";
import { FiBookOpen } from "react-icons/fi";
import { LuBrain, LuHeadphones } from "react-icons/lu";

import { useAuth } from "../context/AuthContext";
import { upsertUserProfileAfterOAuth } from "../data/accountRepo";

/**
 * Keep these in ONE place and bump when docs change materially.
 */
const TERMS_VERSION = "2025-12-22";
const PRIVACY_VERSION = "2025-12-22";
const COOKIE_VERSION = "2025-12-22";
const MARKETING_CONSENT_VERSION = "2025-12-22";

export default function CompleteSignupPage() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const [consent, setConsent] = useState({
    acceptTermsPrivacy: false,
    marketingOptIn: false,
  });
  const [error, setError] = useState("");

  const profile = useMemo(() => {
    return {
      uid: user?.uid || "",
      name: user?.displayName || "",
      email: user?.email || "",
      providerIds: user?.providerData?.map((p) => p.providerId) || [],
    };
  }, [user]);

  const handleConsentChange = (e) => {
    const { name, checked } = e.target;
    setConsent((prev) => ({ ...prev, [name]: checked }));
  };

  const handleComplete = async (e) => {
    e.preventDefault();
    setError("");

    if (!user) {
      setError("Sua sessão expirou. Faça login novamente.");
      navigate("/signin", { replace: true });
      return;
    }

    if (!consent.acceptTermsPrivacy) {
      setError("Para continuar, você precisa aceitar os Termos e a Política de Privacidade.");
      return;
    }

    try {
      await upsertUserProfileAfterOAuth({
        uid: user.uid,
        name: user.displayName || "",
        email: user.email || "",
        consent: {
          acceptTermsPrivacy: consent.acceptTermsPrivacy,
          marketingOptIn: consent.marketingOptIn,
        },
        versions: {
          terms: TERMS_VERSION,
          privacy: PRIVACY_VERSION,
          cookie: COOKIE_VERSION,
          marketing: MARKETING_CONSENT_VERSION,
        },
      });

      navigate("/home", { replace: true });
    } catch (err) {
      console.error("CompleteSignup: error saving consents", err);
      setError("Erro ao finalizar cadastro. Tente novamente.");
    }
  };

  const handleBackToSignin = async () => {
    // Optional: if user reached here by accident, let them back out
    // Signing out avoids leaving an OAuth-authenticated account stuck without consent.
    try {
      await signOut();
    } catch {}
    navigate("/signin", { replace: true });
  };

  // If no user, show minimal message + link back
  if (!user) {
    return (
      <div className="sign-container">
        <div className="sign-content">
          <div className="sign-left">
            <header className="sign-hero">
              <h1 className="sign-hero-title">
                Finalize seu <span className="shiny-text">Cadastro</span>
              </h1>
              <p className="sign-hero-subtitle">Faça login novamente para continuar.</p>
            </header>

            <div className="sign-card">
              <p className="sign-subtitle">Sua sessão não está ativa.</p>
              <button className="sign-button" onClick={() => navigate("/signin", { replace: true })}>
                Ir para Entrar
              </button>
            </div>
          </div>

          <div className="sign-right">
            <div className="sign-feature">
              <div className="sign-feature-icon">
                <FiBookOpen />
              </div>
              <div>
                <h3 className="sign-feature-title">Aprenda com histórias</h3>
                <p className="sign-feature-text">
                  Aprende enquanto consome histórias e ideias que realmente interessam.
                </p>
              </div>
            </div>

            <div className="sign-feature">
              <div className="sign-feature-icon">
                <LuHeadphones />
              </div>
              <div>
                <h3 className="sign-feature-title">Treine seu ouvido</h3>
                <p className="sign-feature-text">
                  Áudio sincronizado em cada história para melhorar a compreensão.
                </p>
              </div>
            </div>

            <div className="sign-feature">
              <div className="sign-feature-icon">
                <LuBrain />
              </div>
              <div>
                <h3 className="sign-feature-title">Expanda seu vocabulário</h3>
                <p className="sign-feature-text">
                  Salve palavras importantes que você ainda não conhece e revise no seu ritmo com
                  repetição espaçada inteligente.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sign-container">
      <div className="sign-content">
        <div className="sign-left">
          <header className="sign-hero">
            <h1 className="sign-hero-title">
              Finalize seu <span className="shiny-text">Cadastro</span>
            </h1>
            <p className="sign-hero-subtitle">
              Só falta aceitar os termos para começar. (Google: {profile.email || "—"})
            </p>
          </header>

          <div className="sign-card">
            <form className="sign-form" onSubmit={handleComplete}>
              <div className="sign-consents">
                <label className="sign-checkbox">
                  <input
                    type="checkbox"
                    name="acceptTermsPrivacy"
                    checked={consent.acceptTermsPrivacy}
                    onChange={handleConsentChange}
                  />
                  <span>
                    Eu aceito os{" "}
                    <a href="/terms-and-conditions" className="sign-link">
                      Termos e Condições
                    </a>{" "}
                    e a{" "}
                    <a href="/privacy-policy" className="sign-link">
                      Política de Privacidade
                    </a>
                    .
                  </span>
                </label>

                <label className="sign-checkbox">
                  <input
                    type="checkbox"
                    name="marketingOptIn"
                    checked={consent.marketingOptIn}
                    onChange={handleConsentChange}
                  />
                  <span>Quero receber novidades e lançamentos.</span>
                </label>
              </div>

              {error && <p className="error">{error}</p>}

              <button type="submit" className="sign-button">
                Continuar
              </button>

              <button
                type="button"
                className="sign-secondary-button"
                onClick={handleBackToSignin}
              >
                Voltar
              </button>
            </form>
          </div>
        </div>

        <div className="sign-right">
          <div className="sign-feature">
            <div className="sign-feature-icon">
              <FiBookOpen />
            </div>
            <div>
              <h3 className="sign-feature-title">Aprenda com histórias</h3>
              <p className="sign-feature-text">
                Aprende enquanto consome histórias e ideias que realmente interessam.
              </p>
            </div>
          </div>

          <div className="sign-feature">
            <div className="sign-feature-icon">
              <LuHeadphones />
            </div>
            <div>
              <h3 className="sign-feature-title">Treine seu ouvido</h3>
              <p className="sign-feature-text">
                Áudio sincronizado em cada história para melhorar a compreensão.
              </p>
            </div>
          </div>

          <div className="sign-feature">
            <div className="sign-feature-icon">
              <LuBrain />
            </div>
            <div>
              <h3 className="sign-feature-title">Expanda seu vocabulário</h3>
              <p className="sign-feature-text">
                Salve palavras importantes que você ainda não conhece e revise no seu ritmo com
                repetição espaçada inteligente.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
